/**
 * MotionSettingsPanel — live controls for the arrival/taxi/tow/departure motion
 * sim. Doubles as the verification surface: the readout shows how many aircraft
 * are taxiing/flying and how many tugs are active at the current clock time, so
 * you can confirm the simulation is doing what you expect during a demo.
 */
import { useState } from 'react'
import { Plane, ChevronDown, ChevronUp } from 'lucide-react'
import { useEventPlannerStore } from '../store/eventPlannerStore'
import { SLOT_MS } from '../data/eventWindow'
import { countActiveActors, type MovementPlan } from '../motion/movementPlan'

interface MotionSettingsPanelProps {
  plans: Record<string, MovementPlan>
}

function Toggle({ label, on, onClick }: { label: string; on: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center justify-between rounded-md border px-2 py-1 text-[11px] font-medium transition-colors ${
        on ? 'border-sky-400/40 bg-sky-500/20 text-sky-100' : 'border-white/10 bg-white/5 text-white/50 hover:text-white'
      }`}
    >
      {label}
      <span className={`ml-2 h-2 w-2 rounded-full ${on ? 'bg-sky-300' : 'bg-white/20'}`} />
    </button>
  )
}

function Slider({
  label,
  value,
  min,
  max,
  step,
  suffix,
  onChange,
}: {
  label: string
  value: number
  min: number
  max: number
  step: number
  suffix: string
  onChange: (v: number) => void
}) {
  return (
    <label className="block">
      <div className="mb-0.5 flex items-center justify-between text-[10px] text-white/50">
        <span>{label}</span>
        <span className="tabular-nums text-white/70">
          {value}
          {suffix}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="h-1 w-full cursor-pointer accent-sky-400"
      />
    </label>
  )
}

export function MotionSettingsPanel({ plans }: MotionSettingsPanelProps) {
  const [open, setOpen] = useState(false)
  const cfg = useEventPlannerStore((s) => s.motionConfig)
  const setCfg = useEventPlannerStore((s) => s.setMotionConfig)
  const currentTime = useEventPlannerStore((s) => s.currentTime)

  const { movingAircraft, activeTugs, activeFuelTrucks } = countActiveActors(plans, currentTime)

  return (
    <div className="pointer-events-auto w-56 rounded-lg border border-white/10 bg-black/70 text-white backdrop-blur-sm">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between px-3 py-2"
      >
        <span className="flex items-center gap-1.5 text-xs font-semibold">
          <Plane className="h-3.5 w-3.5 text-sky-300" />
          Motion
        </span>
        <span className="flex items-center gap-2 text-[10px] text-white/60">
          <span className="tabular-nums">{movingAircraft} taxiing</span>
          <span className="tabular-nums text-amber-300">{activeTugs} tugs</span>
          <span className="tabular-nums text-emerald-300">{activeFuelTrucks} fuel</span>
          {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        </span>
      </button>

      {open && (
        <div className="space-y-3 border-t border-white/10 px-3 py-2.5">
          <div className="grid grid-cols-2 gap-1.5">
            <Toggle label="Animate" on={cfg.enableAnimation} onClick={() => setCfg({ enableAnimation: !cfg.enableAnimation })} />
            <Toggle label="Tow" on={cfg.enableTow} onClick={() => setCfg({ enableTow: !cfg.enableTow })} />
            <Toggle label="Airborne" on={cfg.enableAirborneFinal} onClick={() => setCfg({ enableAirborneFinal: !cfg.enableAirborneFinal })} />
            <Toggle label="Rwy roll" on={cfg.enableRunwayRoll} onClick={() => setCfg({ enableRunwayRoll: !cfg.enableRunwayRoll })} />
            <Toggle label="Fuel" on={cfg.enableFuelTrucks} onClick={() => setCfg({ enableFuelTrucks: !cfg.enableFuelTrucks })} />
            <Toggle label="Paths" on={cfg.enablePathRibbons} onClick={() => setCfg({ enablePathRibbons: !cfg.enablePathRibbons })} />
            <Toggle label="Throttle" on={cfg.throttleModelInsertion} onClick={() => setCfg({ throttleModelInsertion: !cfg.throttleModelInsertion })} />
          </div>

          {cfg.throttleModelInsertion && (
            <>
              <Slider
                label="Cached insert delay"
                value={cfg.modelInsertCachedDelayMs}
                min={0}
                max={500}
                step={25}
                suffix="ms"
                onChange={(v) => setCfg({ modelInsertCachedDelayMs: v })}
              />
              <Slider
                label="Network insert delay"
                value={cfg.modelInsertLoadDelayMs}
                min={0}
                max={1500}
                step={50}
                suffix="ms"
                onChange={(v) => setCfg({ modelInsertLoadDelayMs: v })}
              />
            </>
          )}

          <Slider
            label="Approach lead"
            value={Math.round(cfg.approachLeadMs / SLOT_MS)}
            min={1}
            max={16}
            step={1}
            suffix=" slots"
            onChange={(v) => setCfg({ approachLeadMs: v * SLOT_MS })}
          />
          <Slider
            label="Exit tail"
            value={Math.round(cfg.exitTailMs / SLOT_MS)}
            min={1}
            max={16}
            step={1}
            suffix=" slots"
            onChange={(v) => setCfg({ exitTailMs: v * SLOT_MS })}
          />
          <Slider
            label="Tow if wingspan ≥"
            value={cfg.towWingspanFt}
            min={30}
            max={120}
            step={5}
            suffix=" ft"
            onChange={(v) => setCfg({ towWingspanFt: v })}
          />
          {cfg.enableRunwayRoll && (
            <>
              <Slider
                label="Roll share of airborne"
                value={Math.round(cfg.rollFraction * 100)}
                min={0}
                max={60}
                step={5}
                suffix="%"
                onChange={(v) => setCfg({ rollFraction: v / 100 })}
              />
              <Slider
                label="Takeoff roll length"
                value={Math.round(cfg.takeoffRollFraction * 100)}
                min={20}
                max={90}
                step={5}
                suffix="% rwy"
                onChange={(v) => setCfg({ takeoffRollFraction: v / 100 })}
              />
            </>
          )}
        </div>
      )}
    </div>
  )
}
